import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import moment from 'moment';
import { useSnackbar } from 'notistack';

import { Container, Paper, TextField, Checkbox, FormControlLabel } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';

import SubscriberTable from '../../../components/Subscriber/SubscriberTable';
import BasicModal from '../../../components/shared/Modal/BasicModal';
import { useFetchSubscribersWithOrder } from '../../../shared/subscriber/hooks/subscriber';
import plurialFormatter from '../../../utils/plurialFormatter';

const useStyles = makeStyles(() => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',

    '& > div': {
      display: 'flex',
      alignItems: 'center',
      gap: 15,
    },
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    padding: '1rem',
    marginBottom: '1rem',

    '& > h4': {
      margin: '0 0 0.5rem 0',
    },
  },
}));

export default function SubscriberOrdersList() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const { subscriberType } = useParams();

  const today = moment().format('YYYY-MM-DD');
  const [deliveryDate, setDeliveryDate] = useState(today);
  const [duplicateAt, setDuplicateAt] = useState(moment().add(1, 'week').format('YYYY-MM-DD'));
  const [isForceDate, setIsForceDate] = useState(false);

  const { loading, subscribers, duplicateOrdersFn } = useFetchSubscribersWithOrder({ deliveryDate, subscriberType });
  const [selectedSubscribers, setSelectedSubscribers] = useState([]);

  const handleDeliveryDateChange = (event) => {
    setDeliveryDate(event.target.value);
  };

  const handleCheckAll = (event) => {
    const { checked } = event.target;
    setSelectedSubscribers(checked ? subscribers.map((subscriber) => subscriber._id) : []);
  };

  const handleCheckOne = (event) => {
    const { checked, value } = event.target;
    setSelectedSubscribers((prev) => (checked ? [...prev, value] : prev.filter((id) => id !== value)));
  };

  const handleDuplicateDeliveryDateChange = (event) => {
    setDuplicateAt(event.target.value);
  };

  const handleForceDateChange = (event) => {
    setIsForceDate(event.target.checked);
  };

  const modalContent = useMemo(() => {
    return isForceDate
      ? 'Vous allez dupliquer les commandes des utilisateurs sélectionnés à la date suivante'
      : 'Vous allez dupliquer les commandes des utilisateurs sélectionnés. Elles seront générées automatiquement une semaine après la date de livraison sélectionnée.';
  }, [isForceDate]);

  const handleSubmit = async () => {
    const dateToUse = isForceDate ? duplicateAt : undefined;
    const response = await duplicateOrdersFn(selectedSubscribers, dateToUse);

    if (response.status !== 200) {
      return enqueueSnackbar('Une erreur est survenue lors de la duplication des commandes', {
        variant: 'error',
      });
    }

    const { result } = response.data.data;

    const isFullSuccess = Object.values(result).every((value) => value === true);

    if (isFullSuccess) {
      return enqueueSnackbar('Toutes les commandes ont été dupliquées', {
        variant: 'success',
      });
    }

    const duplicateOrderLen = Object.values(result).filter((value) => value === true).length;
    if (duplicateOrderLen) {
      enqueueSnackbar(`${duplicateOrderLen} commandes ont été dupliquées`, {
        variant: 'success',
      });
    }
    Object.entries(result).forEach(([subscriberId, response]) => {
      const matchingSubscriber = subscribers.find((subscriber) => subscriber._id === subscriberId);
      const matchingSubscriberFullname = `${matchingSubscriber.lastname} ${matchingSubscriber.firstname}`;

      if (response === 'No order found') {
        return enqueueSnackbar(`Aucune commande trouvée pour l'utilisateur ${matchingSubscriberFullname}`, {
          variant: 'error',
        });
      }

      if (response === 'Order duplication failed') {
        return enqueueSnackbar(
          `La duplication de la commande de l'utilisateur ${matchingSubscriberFullname} a échoué`,
          {
            variant: 'error',
          }
        );
      }
    });
  };

  return (
    <Container maxWidth="xl" className={classes.root}>
      <div className={classes.header}>
        <h1>Gestion des utilisateurs abonnés</h1>
        <div>
          {selectedSubscribers.length > 0 && (
            <p>
              {selectedSubscribers.length}{' '}
              {plurialFormatter(selectedSubscribers.length, 'abonné sélectionné', 'abonnés sélectionnés')}
            </p>
          )}
          <BasicModal
            isDisabled={loading || !selectedSubscribers.length}
            btnLabel="Dupliquer les commandes"
            modalContent={modalContent}
            onSubmit={handleSubmit}
          >
            <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <FormControlLabel
                control={<Checkbox checked={isForceDate} onChange={handleForceDateChange} color="primary" />}
                label="Forcer la date de livraison"
              />
              {isForceDate && (
                <TextField
                  label="Date de livraison pour la duplication"
                  type="date"
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={duplicateAt}
                  onChange={handleDuplicateDeliveryDateChange}
                />
              )}
            </div>
          </BasicModal>
        </div>
      </div>
      <Paper className={classes.actions}>
        <h4>Filtrer les commandes :</h4>
        <div>
          <TextField
            label="Date de livraison"
            type="date"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            value={deliveryDate}
            onChange={handleDeliveryDateChange}
          />
        </div>
      </Paper>
      {loading && <Skeleton height={250} />}
      {!loading &&
        (subscribers.length > 0 ? (
          <SubscriberTable
            selectedSubscribers={selectedSubscribers}
            subscribers={subscribers.filter((subscriber) => subscriber.order)}
            onCheckAll={handleCheckAll}
            onCheckOne={handleCheckOne}
          />
        ) : (
          <p>Aucun utilisateur trouvé à cette date</p>
        ))}
    </Container>
  );
}
